import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import configJson from 'config.json';
import { TenantId } from '@tenants/types';
import { getTenantById } from '@tenants/manager';
import { ThemeConfig, ThemeName } from 'themes';

/**
 * Represents the context type for the ThemeContext.
 * This type defines the shape of the theme context data that will be available throughout the application.
 *
 * @property tenantId The ID of the current tenant (internal use only)
 * @property themeName The name of the current theme
 * @property theme The complete theme configuration object
 */
export type ThemeContextType = {
  /** DO NOT USE THIS PROPERTY. It is only used to create the context. */
  tenantId: TenantId;
  /** The name of the current theme, as defined in the tenant configuration. */
  themeName: ThemeName;
  /** The complete theme configuration object, as defined in the tenant configuration. */
  theme: ThemeConfig;
};

/**
 * Creates a React context for managing theme-related data throughout the application.
 * The context provides access to the current theme configuration and theme name.
 */
// Default left undefined; useThemeContext computes the fallback lazily. Computing it here at module load crashed Next.js 16 dev by reading tenant data before tenant files had finished loading.
export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * A custom hook that provides access to the current theme context.
 * This hook should be used to access theme-related data in any component that needs it.
 *
 * @returns {ThemeContextType} The current theme context containing theme configuration and name
 * @throws {Error} If used outside of a ThemeContextProvider
 */
let fallback: ThemeContextType | undefined;
export function useThemeContext(): ThemeContextType {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  fallback ??= createThemeContext();
  return fallback;
}

/**
 * Props for the ThemeContextProvider component.
 *
 * @property children React nodes to be wrapped by the theme context
 * @property tenantId Optional tenant ID to override the default tenant
 */
type ThemeContextProviderProps = PropsWithChildren<{
  tenantId?: TenantId;
}>;

/**
 * A provider component that makes theme data available to all child components.
 * It initializes the theme context based on the provided tenant ID or uses the default tenant.
 */
export default function ThemeContextProvider({
  children,
  tenantId = configJson.defaultTenantId as TenantId,
}: ThemeContextProviderProps) {
  const context = useMemo(() => createThemeContext(tenantId), [tenantId]);

  return (
    <ThemeContext.Provider value={context}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Creates a theme context object based on the provided tenant ID.
 * This function retrieves the tenant configuration and extracts theme-related data.
 *
 * @param tenantId The tenant ID to create the theme context for. Defaults to the default tenant ID from config
 * @returns A theme context object containing tenant ID, theme name, and theme configuration
 */
function createThemeContext(tenantId: TenantId = configJson.defaultTenantId as TenantId) {
  const tenant = getTenantById(tenantId);
  return {
    tenantId,
    themeName: tenant?.theme?.id as ThemeName,
    theme: tenant?.theme as ThemeConfig,
  };
}